/* eslint-disable camelcase */
/* global smily */

// This file requires the following components:
// - rentals-form-pagination

class RentalsForm extends HTMLElement {
  constructor() {
    super();

    this.updateEvent = new CustomEvent('update');

    if (!this.rentalsFormResult) return;

    // store initial inner html
    this.initialInnerHTML = this.innerHTML;

    // store current state
    window.history.replaceState({}, '', new URL(window.location).toString());

    // restore state on browser action
    window.addEventListener('popstate', () => {
      // avoid conflicts with smily modal popstate
      if (document.location.hash) return;

      this.innerHTML = this.initialInnerHTML;
      this.getResults(this.getSearchParams(), true);
      this.initSubmit();
      // this.dispatchEvent(this.updateEvent);
    });

    // First load
    document.addEventListener('DOMContentLoaded', () => {
      this.getResults(this.getSearchParams(), true);
    });
  }

  get formInputs() {
    return this.querySelectorAll('rentals-form-input');
  }

  get options() {
    return [...this.formInputs].map((input) => input.options);
  }

  get rentalsFormResult() {
    return document.querySelector('[data-rentals-form-results]');
  }

  get rentalsSearchHero() {
    return document.querySelector('[data-rentals-form-hero]');
  }

  connectedCallback() {
    this.initSubmit();
    this.initLinks();
  }

  /**
   * @param {boolean} selected
   * @returns {Object}
   * @description Get values from options
  */
  valuesFromOptions(selected = false) {
    let values = {};
    const dataParams = this.getAttribute('data-params');

    if (dataParams) {
      const params = JSON.parse(dataParams);
      values = params;
    }

    this.formInputs.forEach((input) => {
      const options = selected ? input.optionsSelected : input.options;

      if (options) {
        options.forEach((option) => {
          const { name } = option;

          if (!name) return;

          if (!values[name]) values[name] = [];

          values[name].push(option.value);
        });
      }
    });

    return values;
  }

  /**
   * @returns {Array}
   * @description Get options names as an Array
  */
  getOptionsNames() {
    const names = [];

    this.formInputs.forEach((input) => {
      const { options } = input;

      if (options) {
        options.forEach((option) => {
          const { name } = option;

          if (!name) return;

          if (!names.includes(name)) names.push(name);
        });
      }
    });

    return names;
  }

  /**
   * @param {string} kindName
   * @returns {Object}
   * @description Get selected options names as an Object
   */
  selectedKindNames(kindName) {
    const names = [];

    this.formInputs.forEach((input) => {
      const { options } = input;

      if (options) {
        options.forEach((option) => {
          const { name, kind, selected } = option;

          if (!name || kind !== kindName || !selected) return;

          if (!names.includes(name)) names.push(name);
        });
      }
    });

    return names;
  }

  initSubmit() {
    this.querySelectorAll('[type=submit]').forEach((input) => {
      input.addEventListener('click', () => {
        this.submit();
      });
    });
  }

  initLinks() {
    if (!this.rentalsFormResult) return;

    const links = document.querySelectorAll('[data-rentals-form-links] a:not(.direct)');
    links.forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        const params = this.getSearchParams(event.currentTarget.href);
        this.getResults(params);
      });
    });
  }

  submit(query = {}) {
    this.dispatchEvent(new CustomEvent('submit'));

    if (this.rentalsFormResult) {
      const params = { ...this.getSearchParams(), ...query };

      // filter params to avoid conflicts with options and keep only
      // params that are not in options
      const optionsNames = this.getOptionsNames();
      const filteredData = Object.keys(params)
        .filter((key) => !optionsNames.includes(key))
        .reduce((obj, key) => {
          // eslint-disable-next-line no-param-reassign
          obj[key] = params[key];
          return obj;
        }, {});

      if (filteredData.page) delete filteredData.page;

      this.getResults(filteredData);
    } else {
      // open search page
      const url = new URL(smily.website.searchUrl, window.location.origin);
      const searchParams = Object.assign(query, this.valuesFromOptions(true));
      url.search = new URLSearchParams(smily.paramsToRailsFormat(searchParams));

      if (smily.isDebug) url.searchParams.set('debug', smily.isDebug);
      if (smily.variant) url.searchParams.set('variant', smily.variant);

      window.location = url.toString();
    }
  }

  getTrackedParams() {
    const searchFilters = this.valuesFromOptions(true);
    const {
      checkin, checkout, adults, children, min_price, max_price,
    } = searchFilters;

    const params = new URL(document.location.href).searchParams;

    return {
      // params: new URLSearchParams(params).toString(),
      destination_ids: searchFilters['destination[]'],
      amenities: this.selectedKindNames('amenity'),
      rentals_types: searchFilters['rental_type[]'],
      booking_options: this.selectedKindNames('booking_option'),
      checkin: checkin?.[0],
      checkout: checkout?.[0],
      adults: adults?.[0],
      children: children?.[0],
      min_price: min_price?.[0],
      max_price: max_price?.[0],
      sort_by: searchFilters.sort_by?.[0],
      page: params.get('page') || '1',
      url: document.location.href,
    };
  }

  trackQueries() {
    // Check destinations and amenities arrays
    try {
      smily.analytics.tracker.trackSearchSubmit(this.getTrackedParams());
    } catch (error) {
      // silence
    }
  }

  trackDisplayRentals(rentals) {
    if (!rentals || !rentals.length) return;

    try {
      const rentalIds = rentals.map((rental) => rental.id);
      smily.analytics.tracker.trackDisplayRentals(rentalIds, this.getTrackedParams());
    } catch (error) {
      // silence
    }
  }

  async loadPage(filters, current, disablePushHistory) {
    // add cards placeholders
    this.rentalsFormResult.insertAdjacentHTML('beforeend', smily.RentalsCardsPlacholders());

    let sameDestinations;

    // request after first page load
    if (!disablePushHistory) {
      // get destination ids from the original search
      if (this.dataset.selectedDestinations) {
        const destinationIds = JSON.parse(this.dataset.selectedDestinations);
        const newDestinationIds = filters['destination[]'];

        sameDestinations = destinationIds.sort().toString() === newDestinationIds.sort().toString();
      }

      if (this.rentalsSearchHero && !sameDestinations) {
        // add css transition to animate height
        this.rentalsSearchHero.style.transition = 'height 0.3s ease-in-out';
        // animate height to 0 and hide
        this.rentalsSearchHero.style.height = `${this.rentalsSearchHero.offsetHeight}px`;
        setTimeout(() => {
          this.rentalsSearchHero.style.height = '0px';
        }, 0);
        // wait for animation to finish
        // eslint-disable-next-line no-promise-executor-return
        await new Promise((resolve) => setTimeout(resolve, 300));
        // hide elements
        this.rentalsSearchHero.classList.add('d-none');
      }

      // scroll to top
      const { offsetTop } = document.querySelector('[role="main"]');
      const additionalOffset = this.rentalsSearchHero?.offsetHeight || 0;

      window.scrollTo(0, window.scrollY === 0 ? 0 : offsetTop + additionalOffset);
    }

    const { pathname } = new URL(document.location);

    let rentalsListUrl;

    const isCampaignPage = pathname.includes('/campaigns/');

    if (isCampaignPage) {
      // get campaign slug
      const [campaignSlug] = pathname.split('/campaigns/')[1].split('/');
      rentalsListUrl = smily.website.rentalsCampaignsListPath.replace('#', campaignSlug);
    }

    // fetch data
    const params = { ...filters, page: current };
    const data = await smily.rentalsList(params, rentalsListUrl);

    // remove cards placeholders
    this.rentalsFormResult.querySelectorAll('.card-placeholder').forEach((e) => e.remove());

    // insert new cards
    this.rentalsFormResult.insertAdjacentHTML('beforeend', smily.RentalsCardsList(data.rentals, 'rentalsForm'));

    // insert pagination
    const { pagination } = data.meta;
    document.querySelector('rentals-form-pagination')?.paginate(pagination, filters);

    // store new state
    if (!disablePushHistory) {
      const redirectToSearchUrl = !pathname.includes('/rentals/search/') && !sameDestinations;

      // update url
      const url = new URL(
        redirectToSearchUrl ? smily.website.searchPath : smily.website.searchUrl,
        window.location.origin,
      );

      url.search = new URLSearchParams(smily.paramsToRailsFormat(filters));

      if (smily.isDebug) url.searchParams.set('debug', smily.isDebug);
      if (smily.variant) url.searchParams.set('variant', smily.variant);

      window.history.pushState({}, '', url.toString());

      if (redirectToSearchUrl) {
        // set default search page title
        const title = document.querySelector('meta[name="rentals-search-page-title"]')?.content;
        if (title) document.title = title;
      }
    }

    // FIXME: this is a hack to fix the issue with the destination id missing on first load
    this.trackQueries();
    this.trackDisplayRentals(data.rentals);
    this.initLinks();
    this.dispatchEvent(this.updateEvent);
  }

  // convert query to object
  getResults(query = {}, disablePushHistory = false, clearResults = true) {
    // set clear results to false for infinite scroll pagination
    if (clearResults) this.rentalsFormResult.innerHTML = '';

    const valuesFromOptions = this.valuesFromOptions(true);
    const filters = { ...query, ...valuesFromOptions };
    const page = Number(filters.page || 1);

    // If on Categories and Destinations page we dont have destination input
    if (!valuesFromOptions['destination[]'] && this.dataset.selectedDestinations) {
      filters['destination[]'] = JSON.parse(this.dataset.selectedDestinations);
    }

    this.loadPage(filters, page, disablePushHistory);

    const { controller } = smily.website.currentPage;
    smily.trackers.ga('event', controller === 'search' ? 'generic' : controller, { event_category: 'search' });
    smily.trackers.fbq('track', 'Search', filters);
  }

  getSearchParams(url = document.location) {
    const { searchParams } = new URL(url);
    return Object.fromEntries([...searchParams]);
  }
}

customElements.define('rentals-form', RentalsForm);
